/**
 * Serviço Price Activity — sync de diffs + leituras para UI.
 * Ponto de troca futuro: ApiPriceActivityRepository com a mesma interface.
 */

import {
  isFairtradeCollectionHub,
  type FairtradePowerRow,
} from '@/lib/fairtrade-catalog';
import {
  PRICE_ACTIVITY_MAX_PER_CATEGORY,
  PRICE_ACTIVITY_RETENTION_DAYS,
} from '@/lib/price-activity-config';
import {
  diffRowAgainstSnapshot,
  isDuplicateChange,
  snapshotFromRow,
  sortChangesNewestFirst,
  summarizeCategoryChanges,
} from '@/lib/price-activity';
import type {
  PriceActivityCategorySummary,
  PriceActivityChange,
} from '@/lib/price-activity-types';
import { getPriceActivityRepository } from '@/services/priceActivityStorage';

export type SyncPriceActivityResult = {
  seeded: boolean;
  added: number;
};

let syncInflight: Promise<SyncPriceActivityResult> | null = null;
let lastSyncToken = 0;

/** Listeners para a UI actualizar badges após sync. */
type SyncListener = () => void;
const syncListeners = new Set<SyncListener>();

export function subscribePriceActivitySync(listener: SyncListener): () => void {
  syncListeners.add(listener);
  return () => syncListeners.delete(listener);
}

function notifySyncListeners() {
  for (const listener of syncListeners) {
    try {
      listener();
    } catch {
      /* ignore */
    }
  }
}

/**
 * Compara o catálogo actual com snapshots locais e regista alterações reais.
 * Primeira visita: apenas seed (sem changes).
 */
export async function syncPriceActivityFromCatalog(
  rows: FairtradePowerRow[]
): Promise<SyncPriceActivityResult> {
  if (syncInflight) return syncInflight;

  const run = (async (): Promise<SyncPriceActivityResult> => {
    const repo = getPriceActivityRepository();
    const tradeable = rows.filter((r) => !isFairtradeCollectionHub(r));
    const now = new Date().toISOString();
    const existing = await repo.getAllSnapshots();
    const existingById = new Map(existing.map((s) => [s.powerId, s]));
    const isSeed = existing.length === 0;

    const nextSnapshots = tradeable.map((row) => snapshotFromRow(row, now));
    const newChanges: PriceActivityChange[] = [];

    if (!isSeed) {
      const allChanges = await repo.listChanges();
      const latestByPower = new Map<number, (typeof allChanges)[number]>();
      for (const c of allChanges) {
        const prev = latestByPower.get(c.powerId);
        if (
          !prev ||
          new Date(c.timestamp).getTime() > new Date(prev.timestamp).getTime()
        ) {
          latestByPower.set(c.powerId, c);
        }
      }

      for (const row of tradeable) {
        const prev = existingById.get(row.id);
        if (!prev) {
          // Power novo no catálogo — snapshot sem change.
          continue;
        }
        const change = diffRowAgainstSnapshot(row, prev, now);
        if (!change) continue;
        const latest = latestByPower.get(row.id);
        if (isDuplicateChange(latest, change)) continue;
        change.id = `${row.id}-${now}-${newChanges.length}`;
        newChanges.push(change);
      }
    }

    await repo.putSnapshots(nextSnapshots);
    await repo.deleteSnapshotsNotIn(tradeable.map((r) => r.id));

    if (newChanges.length > 0) {
      await repo.addChanges(newChanges);
    }

    await repo.trimChanges({
      retentionDays: PRICE_ACTIVITY_RETENTION_DAYS,
      maxPerCategory: PRICE_ACTIVITY_MAX_PER_CATEGORY,
    });

    lastSyncToken += 1;
    notifySyncListeners();

    return { seeded: isSeed, added: newChanges.length };
  })();

  syncInflight = run.finally(() => {
    syncInflight = null;
  });

  return syncInflight;
}

export function getPriceActivitySyncToken(): number {
  return lastSyncToken;
}

export async function listPriceActivityByCategory(
  category: string
): Promise<PriceActivityChange[]> {
  const repo = getPriceActivityRepository();
  const list = await repo.listChangesByCategory(category);
  return sortChangesNewestFirst(list);
}

export async function getCategoryActivitySummary(
  category: string
): Promise<PriceActivityCategorySummary> {
  const changes = await listPriceActivityByCategory(category);
  return summarizeCategoryChanges(category, changes);
}

export async function getAllCategoryActivitySummaries(
  categories: string[]
): Promise<Map<string, PriceActivityCategorySummary>> {
  const repo = getPriceActivityRepository();
  const all = await repo.listChanges();
  const map = new Map<string, PriceActivityCategorySummary>();

  for (const category of categories) {
    const subset = all.filter((c) => c.category === category);
    map.set(category, summarizeCategoryChanges(category, subset));
  }

  return map;
}
